import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import {
  ArrowLeft,
  ArrowRight,
  Calendar,
  MapPin,
  Ticket,
  Send,
  CheckCircle2,
  Music2,
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * /submit-gig — public gig submission form for in-rotation bands.
 *
 * Flow:
 *  1. Band picks themselves from the /api/family/bands dropdown
 *  2. Fills venue, city, date, tickets URL, notes
 *  3. Submits — lands in the admin inbox as ``status: pending``
 *  4. Admin approves from /admin → the gig appears on /family
 *
 * No auth (deliberately friction-free — bands rarely want another login).
 * Submitter email is optional but recommended for admin follow-up.
 */
export default function SubmitGig() {
  const [bands, setBands] = useState([]);
  const [loadingBands, setLoadingBands] = useState(true);
  const [form, setForm] = useState({
    band_id: "",
    venue: "",
    city: "",
    state_country: "",
    gig_date: "",
    ticket_url: "",
    notes: "",
    submitter_email: "",
  });
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  useEffect(() => {
    let cancelled = false;
    axios
      .get(`${API}/family/bands`)
      .then((res) => {
        if (cancelled) return;
        setBands(Array.isArray(res?.data?.bands) ? res.data.bands : []);
      })
      .catch(() => {
        if (!cancelled) setBands([]);
      })
      .finally(() => {
        if (!cancelled) setLoadingBands(false);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const update = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const canSubmit =
    !!form.band_id &&
    form.venue.trim().length > 0 &&
    form.city.trim().length > 0 &&
    !!form.gig_date &&
    !submitting;

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!canSubmit) return;
    setSubmitting(true);
    try {
      await axios.post(`${API}/gigs/submit`, {
        band_id: form.band_id,
        venue: form.venue.trim(),
        city: form.city.trim(),
        state_country: form.state_country.trim(),
        gig_date: form.gig_date,
        ticket_url: form.ticket_url.trim(),
        notes: form.notes.trim(),
        submitter_email: form.submitter_email.trim(),
      });
      setDone(true);
      toast.success("🎸 Gig submitted — DRR admin will review shortly");
    } catch (err) {
      const msg =
        err?.response?.data?.detail ||
        "Something went wrong submitting your gig. Try again.";
      toast.error(msg);
    } finally {
      setSubmitting(false);
    }
  };

  const resetForm = () => {
    setDone(false);
    setForm({
      band_id: "",
      venue: "",
      city: "",
      state_country: "",
      gig_date: "",
      ticket_url: "",
      notes: "",
      submitter_email: "",
    });
  };

  const todayISO = new Date().toISOString().slice(0, 10);

  return (
    <div
      className="min-h-screen bg-black text-zinc-50 selection:bg-red-600 selection:text-white"
      data-testid="submit-gig-page"
    >
      {/* Brand strip */}
      <div className="border-b border-zinc-800 bg-black">
        <div className="max-w-5xl mx-auto px-4 md:px-8 py-3 flex items-center justify-between gap-3 flex-wrap">
          <Link
            to="/family"
            className="inline-flex items-center gap-2 text-xs tracking-[0.35em] uppercase text-zinc-400 hover:text-red-500 transition-colors font-display"
            data-testid="submit-gig-back-family"
          >
            <ArrowLeft className="w-3.5 h-3.5" strokeWidth={2.5} />
            back_to_the_family
          </Link>
          <div className="text-[10px] tracking-[0.4em] text-zinc-500 uppercase font-display flex items-center gap-2">
            <span className="on-air-dot scale-75" aria-hidden />
            //_POST_A_GIG
          </div>
        </div>
      </div>

      {/* Hero */}
      <section className="relative border-b border-zinc-800 overflow-hidden">
        <div
          aria-hidden
          className="absolute inset-0 opacity-[0.06] pointer-events-none"
          style={{
            backgroundImage:
              "repeating-linear-gradient(-45deg, transparent 0 14px, rgba(220,38,38,1) 14px 16px)",
          }}
        />
        <div className="relative max-w-5xl mx-auto px-4 md:px-8 pt-12 md:pt-16 pb-8 md:pb-10">
          <div className="flex items-center gap-3 mb-4 flex-wrap">
            <span className="text-[10px] md:text-xs tracking-[0.4em] text-red-500 uppercase font-display">
              //_BANDS_ONLY
            </span>
            <span className="h-px w-16 bg-red-600 hidden sm:inline-block" />
            <span className="text-[10px] md:text-xs tracking-[0.4em] text-zinc-500 uppercase font-display">
              POST YOUR OWN SHOWS
            </span>
          </div>
          <h1 className="font-display text-[10vw] md:text-[6vw] lg:text-[5vw] leading-[0.9] tracking-tighter text-zinc-50">
            YOUR STAGE. <span className="text-red-600">OUR SIGNAL.</span>
          </h1>
          <p className="mt-5 max-w-2xl text-base md:text-lg text-zinc-300 leading-snug">
            Post your next gig here and it lands on the DRR Family page for
            everyone tuning in. Zero cost, zero friction, straight to the pit.
          </p>
        </div>
      </section>

      {/* Form / Success */}
      <section className="border-b border-zinc-800 bg-black">
        <div className="max-w-3xl mx-auto px-4 md:px-8 py-14 md:py-20">
          {done ? (
            <div
              className="border-2 border-red-600 bg-zinc-950 p-8 md:p-10 text-center"
              data-testid="submit-gig-success"
            >
              <CheckCircle2 className="w-14 h-14 text-red-600 mx-auto mb-4" strokeWidth={1.8} />
              <h2 className="font-display text-3xl md:text-4xl leading-tight tracking-tight text-zinc-50">
                GIG <span className="text-red-600">RECEIVED</span>.
              </h2>
              <p className="mt-3 text-sm text-zinc-400 max-w-md mx-auto">
                DRR admin will review it and — assuming there&apos;s nothing
                weird — flip it live on the Family page within a day. Rock on.
              </p>
              <div className="mt-6 flex items-center justify-center gap-3 flex-wrap">
                <button
                  type="button"
                  onClick={resetForm}
                  className="inline-flex items-center gap-2 border-2 border-red-600 bg-red-600 text-white hover:bg-white hover:text-black px-5 py-3 text-xs tracking-widest uppercase font-display transition-colors"
                  data-testid="submit-gig-post-another"
                >
                  <Send className="w-3.5 h-3.5" strokeWidth={2.5} />
                  POST ANOTHER GIG
                </button>
                <Link
                  to="/family"
                  className="inline-flex items-center gap-2 border border-zinc-700 hover:border-red-600 px-5 py-3 text-xs tracking-widest uppercase font-display text-zinc-100 hover:text-red-500 transition-colors"
                >
                  VIEW THE FAMILY <ArrowRight className="w-3.5 h-3.5" />
                </Link>
              </div>
            </div>
          ) : (
            <form
              onSubmit={handleSubmit}
              className="border-2 border-zinc-700 bg-zinc-950 p-6 md:p-8 flex flex-col gap-5"
              data-testid="submit-gig-form"
            >
              <FieldLabel icon={Music2} label="Band" required>
                <select
                  value={form.band_id}
                  onChange={(e) => update("band_id", e.target.value)}
                  disabled={loadingBands || bands.length === 0}
                  required
                  className="w-full bg-black border border-zinc-700 focus:border-red-600 outline-none text-sm md:text-base text-zinc-50 px-3 py-2.5 font-display"
                  data-testid="submit-gig-band"
                >
                  <option value="">
                    {loadingBands
                      ? "Loading bands…"
                      : bands.length === 0
                        ? "No bands in rotation yet — submit music first via /submit"
                        : "Pick your band"}
                  </option>
                  {bands.map((b) => (
                    <option key={b.id} value={b.id}>
                      {b.band_name}
                    </option>
                  ))}
                </select>
                {!loadingBands && bands.length === 0 && (
                  <div className="mt-1 text-[11px] text-zinc-500">
                    Bands need to be in DRR rotation first.{" "}
                    <Link to="/submit" className="text-red-400 hover:text-red-500 underline">
                      Submit your music
                    </Link>{" "}
                    to get spinning.
                  </div>
                )}
              </FieldLabel>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <FieldLabel icon={MapPin} label="Venue" required>
                  <input
                    type="text"
                    value={form.venue}
                    onChange={(e) => update("venue", e.target.value)}
                    maxLength={120}
                    required
                    placeholder="e.g. The Whisky a Go Go"
                    className="w-full bg-black border border-zinc-700 focus:border-red-600 outline-none text-sm md:text-base text-zinc-50 px-3 py-2.5 font-display"
                    data-testid="submit-gig-venue"
                  />
                </FieldLabel>
                <FieldLabel icon={Calendar} label="Date" required>
                  <input
                    type="date"
                    value={form.gig_date}
                    onChange={(e) => update("gig_date", e.target.value)}
                    min={todayISO}
                    required
                    className="w-full bg-black border border-zinc-700 focus:border-red-600 outline-none text-sm md:text-base text-zinc-50 px-3 py-2.5 font-display"
                    data-testid="submit-gig-date"
                  />
                </FieldLabel>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <FieldLabel label="City" required>
                  <input
                    type="text"
                    value={form.city}
                    onChange={(e) => update("city", e.target.value)}
                    maxLength={80}
                    required
                    placeholder="e.g. Los Angeles"
                    className="w-full bg-black border border-zinc-700 focus:border-red-600 outline-none text-sm md:text-base text-zinc-50 px-3 py-2.5 font-display"
                    data-testid="submit-gig-city"
                  />
                </FieldLabel>
                <FieldLabel label="State / Country">
                  <input
                    type="text"
                    value={form.state_country}
                    onChange={(e) => update("state_country", e.target.value)}
                    maxLength={80}
                    placeholder="e.g. CA · USA"
                    className="w-full bg-black border border-zinc-700 focus:border-red-600 outline-none text-sm md:text-base text-zinc-50 px-3 py-2.5 font-display"
                    data-testid="submit-gig-state"
                  />
                </FieldLabel>
              </div>

              <FieldLabel icon={Ticket} label="Tickets URL">
                <input
                  type="text"
                  value={form.ticket_url}
                  onChange={(e) => update("ticket_url", e.target.value)}
                  maxLength={400}
                  placeholder="https://tickets.example.com/your-show"
                  className="w-full bg-black border border-zinc-700 focus:border-red-600 outline-none text-sm md:text-base text-zinc-50 px-3 py-2.5 font-display"
                  data-testid="submit-gig-ticket-url"
                />
              </FieldLabel>

              <FieldLabel label="Notes / Extra info">
                <textarea
                  value={form.notes}
                  onChange={(e) => update("notes", e.target.value)}
                  maxLength={400}
                  rows={3}
                  placeholder="Openers, ages, cover charge, VIP details, anything DRR listeners should know…"
                  className="w-full bg-black border border-zinc-700 focus:border-red-600 outline-none text-sm text-zinc-50 px-3 py-2.5 font-display resize-y"
                  data-testid="submit-gig-notes"
                />
                <div className="mt-1 text-[10px] text-zinc-500 tracking-widest uppercase text-right">
                  {form.notes.length}/400
                </div>
              </FieldLabel>

              <FieldLabel label="Your email (for admin follow-up — not published)">
                <input
                  type="email"
                  value={form.submitter_email}
                  onChange={(e) => update("submitter_email", e.target.value)}
                  maxLength={240}
                  placeholder="you@yourband.com"
                  className="w-full bg-black border border-zinc-700 focus:border-red-600 outline-none text-sm md:text-base text-zinc-50 px-3 py-2.5 font-display"
                  data-testid="submit-gig-email"
                />
              </FieldLabel>

              <div className="pt-2 flex items-center justify-between gap-3 flex-wrap">
                <div className="text-[10px] text-zinc-500 tracking-widest uppercase font-display">
                  //_LANDS_IN_ADMIN_INBOX_FOR_REVIEW
                </div>
                <button
                  type="submit"
                  disabled={!canSubmit}
                  className={`inline-flex items-center gap-2 border-2 px-5 py-3 text-xs md:text-sm tracking-widest uppercase font-display transition-colors ${
                    canSubmit
                      ? "border-red-600 bg-red-600 text-white hover:bg-white hover:text-black"
                      : "border-zinc-700 text-zinc-600 cursor-not-allowed"
                  }`}
                  data-testid="submit-gig-submit"
                >
                  {submitting ? (
                    <span className="inline-block w-4 h-4 border-2 border-current border-r-transparent rounded-full animate-spin" />
                  ) : (
                    <Send className="w-4 h-4" strokeWidth={2.5} />
                  )}
                  {submitting ? "SENDING…" : "POST THIS GIG"}
                </button>
              </div>
            </form>
          )}
        </div>
      </section>

      <footer className="border-t border-zinc-800 bg-zinc-950">
        <div className="max-w-5xl mx-auto px-4 md:px-8 py-6 flex items-center justify-between flex-wrap gap-3 text-[10px] tracking-[0.3em] uppercase text-zinc-500 font-display">
          <span>© {new Date().getFullYear()} digital revolution radio</span>
          <span>//_your_stage_our_signal</span>
          <Link
            to="/"
            className="hover:text-red-500 transition-colors"
            data-testid="submit-gig-footer-home"
          >
            ← RETURN TO STATION
          </Link>
        </div>
      </footer>
    </div>
  );
}

function FieldLabel({ icon: Icon, label, required, children }) {
  return (
    <label className="flex flex-col gap-1.5 min-w-0">
      <span className="text-[11px] tracking-[0.3em] uppercase text-red-500 font-display flex items-center gap-1.5">
        {Icon && <Icon className="w-3 h-3" strokeWidth={2.5} />}
        {label}
        {required && <span className="text-zinc-500">·  required</span>}
      </span>
      {children}
    </label>
  );
}
